import { useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronRight, Check, Info, PlayCircle } from "lucide-react";
import PuduVideoPlayer from "@/components/pudu/PuduVideoPlayer";
import PuduQuoteDialog from "@/components/pudu/PuduQuoteDialog";
import MowerComparison from "@/components/pudu/MowerComparison";
import {
  getPuduProduct,
  puduProducts,
  et1DockFunctions,
  et1AdaptiveCleaning,
  et1DockVideo,
  et1DisplayImage,
} from "@/lib/puduCatalog";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics";

const PuduProduct = ({ slug: slugProp }: { slug?: string }) => {
  const params = useParams();
  const slug = slugProp ?? params.slug;
  const product = getPuduProduct(slug);
  const [searchParams] = useSearchParams();
  const [quoteOpen, setQuoteOpen] = useState(searchParams.get("quote") === "1");
  const [variantId, setVariantId] = useState("standard");
  const [lightbox, setLightbox] = useState<{ src: string; caption: string } | null>(null);

  const related = useMemo(
    () => (product ? product.related.map((s) => getPuduProduct(s)).filter(Boolean) : []),
    [product]
  );

  if (!product) return <Navigate to="/robots" replace />;

  const variant = product.variants?.find((v) => v.id === variantId) ?? product.variants?.[0];
  const isMower = ["gt3", "gt5", "gt7"].includes(product.slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.cardSummary,
    category: product.category,
    brand: { "@type": "Brand", name: "PUDU Robotics" },
    image: [product.cardImage],
    additionalProperty: product.specs.map((s) => ({
      "@type": "PropertyValue",
      name: s.label,
      value: s.value,
    })),
  };

  return (
    <>
      <SEO
        title={`${product.name} ${product.subtitle} | Neil AI Global Solutions`}
        description={product.cardSummary}
        canonical={`https://www.neilai.ai/robots/${product.slug}`}
        keywords={`${product.name}, ${product.subtitle}, commercial robotics, Neil AI Global Solutions`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navigation />

      <main className="min-h-screen bg-background pt-32 md:pt-40 pb-20">
        <div className="max-w-6xl mx-auto px-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-foreground">Home</Link></li>
              <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
              <li><Link to="/robots" className="hover:text-foreground">Robots</Link></li>
              <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
              <li>{product.category}</li>
              <li aria-hidden="true"><ChevronRight className="h-3 w-3" /></li>
              <li className="text-foreground font-medium" aria-current="page">{product.name}</li>
            </ol>
          </nav>

          {/* Hero */}
          <section className="mt-6 grid min-w-0 gap-8 lg:grid-cols-2 lg:items-center">
            <Card className="glass-effect border-[hsl(var(--line))] overflow-hidden bg-white/5 p-3 sm:p-5">
              <img
                src={product.heroImage}
                alt={`${product.name} ${product.subtitle} in operation`}
                width={1600}
                height={900}
                fetchPriority="high"
                decoding="async"
                className="mx-auto block max-h-[620px] w-full object-contain"
              />
            </Card>

            <div className="min-w-0">
              <Badge variant="outline">{product.category}</Badge>
              <h1 className="mt-3 break-words font-orbitron text-4xl md:text-5xl font-bold">{product.name}</h1>
              <p className="mt-1 text-lg text-primary">{product.subtitle}</p>
              <p className="mt-4 text-xl font-medium">{product.headline}</p>
              <p className="mt-3 text-muted-foreground">{product.summary}</p>
              <p className="mt-3 text-xs text-muted-foreground">
                Powered by PUDU Robotics. Deployed, trained and supported by Neil AI Global Solutions.
              </p>

              {product.variants && variant && (
                <fieldset className="mt-6">
                  <legend className="text-sm font-semibold">Choose a fork configuration</legend>
                  <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="MP2000 fork configuration">
                    {product.variants.map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        role="radio"
                        aria-checked={variantId === v.id}
                        onClick={() => setVariantId(v.id)}
                        className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                          variantId === v.id
                            ? "border-primary bg-primary/15 text-foreground"
                            : "border-[hsl(var(--line))] text-muted-foreground hover:border-primary/50"
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Model {variant.modelId} — fork outer width {variant.forkOuterWidth}. All other published
                    specifications are shared between variants.
                  </p>
                </fieldset>
              )}

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {product.metrics.map((metric) => (
                  <div key={metric.label} className="rounded-xl border border-[hsl(var(--line))] bg-white/5 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{metric.label}</p>
                    <p className="mt-1 text-sm font-bold text-primary">{metric.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button size="lg" className="rounded-full" onClick={() => setQuoteOpen(true)}>
                  Request a Quote
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full">
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">Book a Demo</a>
                </Button>
                <Button asChild size="lg" variant="ghost" className="rounded-full">
                  <a href="#product-video"><PlayCircle className="mr-2 h-4 w-4" /> Watch Video</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main video */}
          <section id="product-video" className="mt-14 scroll-mt-32">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">{product.name} in action</h2>
            <PuduVideoPlayer
              className="mt-5"
              src={product.mainVideo}
              poster={product.poster}
              title={`${product.name} product video`}
              autoplay
            />

            {product.gallery.length > 0 && (
              <>
                <h3 className="mt-8 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Gallery</h3>
                <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {product.gallery.map((item) => (
                    <li key={item.src} className="min-w-0">
                      <button
                        type="button"
                        onClick={() => setLightbox({ src: item.src, caption: item.caption })}
                        className="block aspect-[4/3] w-full overflow-hidden rounded-xl border border-[hsl(var(--line))] bg-white/5 p-2"
                        aria-label={`Enlarge: ${item.caption}`}
                      >
                        <img
                          src={item.src}
                          alt={item.caption}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-contain"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* Features */}
          <section className="mt-16">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Capabilities</h2>
            <div className="mt-6 space-y-10">
              {product.features.map((feature, i) => (
                <div
                  key={feature.title}
                  className={`grid gap-6 md:grid-cols-2 md:items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}
                >
                  {feature.media && (
                    <div className="overflow-hidden rounded-2xl border border-[hsl(var(--line))] bg-white/5 p-2 sm:p-4">
                      {feature.mediaType === "video" ? (
                        <PuduVideoPlayer src={feature.media} title={`${product.name} — ${feature.title}`} />
                      ) : (
                        <img
                          src={feature.media}
                          alt={`${product.name} — ${feature.title}`}
                          loading="lazy"
                          decoding="async"
                          className="mx-auto block max-h-[620px] w-full object-contain"
                        />
                      )}
                    </div>
                  )}
                  <div>
                    {feature.icon && (
                      <img src={feature.icon} alt="" aria-hidden="true" className="mb-3 h-10 w-10" loading="lazy" />
                    )}
                    <h3 className="font-orbitron text-xl font-bold">{feature.title}</h3>
                    <p className="mt-2 text-muted-foreground">{feature.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ET1 specific sections */}
          {product.slug === "et1" && (
            <>
              <section className="mt-16">
                <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Four cleaning modes</h2>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {["Sweeping", "Scrubbing", "Vacuuming", "Dust-mopping"].map((mode) => (
                    <li key={mode} className="rounded-xl border border-[hsl(var(--line))] bg-white/5 p-4 text-sm font-semibold">
                      <Check className="mb-2 h-4 w-4 text-primary" />
                      {mode}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="mt-14">
                <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Eight-function self-cleaning dock</h2>
                <div className="mt-5 grid gap-6 lg:grid-cols-2 lg:items-start">
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {et1DockFunctions.map((fn) => (
                      <li key={fn.title} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--line))] bg-white/5 p-3">
                        <img src={fn.icon} alt="" aria-hidden="true" className="h-8 w-8 shrink-0" loading="lazy" />
                        <span className="text-sm font-medium">{fn.title}</span>
                      </li>
                    ))}
                  </ul>
                  <PuduVideoPlayer src={et1DockVideo} title="ET1 self-cleaning station" />
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Confirm dock inclusion, water supply, drainage and power during a site assessment.
                </p>
              </section>

              <section className="mt-14">
                <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Adaptive cleaning</h2>
                <div className="mt-5 grid gap-6 lg:grid-cols-2 lg:items-center">
                  <PuduVideoPlayer src={et1AdaptiveCleaning.video} title="ET1 adaptive cleaning" />
                  <ul className="grid gap-3">
                    {et1AdaptiveCleaning.items.map((item) => (
                      <li key={item.title} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--line))] bg-white/5 p-3">
                        <img src={item.icon} alt="" aria-hidden="true" className="h-8 w-8" loading="lazy" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  When ET1 recognizes heavier soil, it raises roller speed, slows travel and increases water output —
                  then lifts the roller at carpet edges and over spills.
                </p>
              </section>

              <section className="mt-14">
                <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Maintenance</h2>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    "Real-time roller self-cleaning during operation",
                    "Dock-handled roller washing, rinsing and spin-drying",
                    "Weekly dustbox emptying with dock dust collection",
                    "Quick-release, washable cleaning components",
                    "Proactive maintenance alerts raised by the robot",
                    "Built-in training system for new staff",
                  ].map((item) => (
                    <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="mt-14">
                <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Optional 21.5-inch advertising display</h2>
                <div className="mt-5 grid gap-6 md:grid-cols-2 md:items-center">
                  <img
                    src={et1DisplayImage}
                    alt="ET1 with the optional 21.5-inch advertising display"
                    loading="lazy"
                    className="w-full rounded-2xl border border-[hsl(var(--line))] bg-white/5 object-contain"
                  />
                  <div>
                    <p className="text-muted-foreground">
                      A 21.5-inch advertising display is available as optional equipment. It is not standard on ET1.
                    </p>
                    <p className="mt-3 flex gap-2 text-sm text-muted-foreground">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      Dimensions and clearances for a display-equipped configuration are not confirmed by the
                      manufacturer's page, so Neil AI verifies them before quoting that build.
                    </p>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* Specifications */}
          <section className="mt-16">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Technical specifications</h2>
            <Card className="mt-5 glass-effect border-[hsl(var(--line))] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">{product.name} technical specifications</caption>
                  <tbody>
                    {variant && (
                      <tr className="border-b border-[hsl(var(--line))] bg-primary/5">
                        <th scope="row" className="p-3 text-left font-semibold">Model / fork outer width</th>
                        <td className="p-3 text-muted-foreground">{variant.modelId} — {variant.forkOuterWidth}</td>
                      </tr>
                    )}
                    {product.specs.map((spec) => (
                      <tr key={spec.label} className="border-b border-[hsl(var(--line))] last:border-0">
                        <th scope="row" className="p-3 text-left font-semibold align-top w-1/2 sm:w-2/5">{spec.label}</th>
                        <td className="p-3 text-muted-foreground">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <ul className="mt-4 space-y-2">
              {product.notes.map((note) => (
                <li key={note} className="flex gap-2 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>{note}</span>
                </li>
              ))}
              <li className="flex gap-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <span>
                  Specifications are manufacturer-published and condition-dependent, recorded from{" "}
                  <a href={product.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline">
                    the manufacturer product page
                  </a>{" "}
                  on {product.verifiedOn}.
                </span>
              </li>
            </ul>
          </section>

          {isMower && <MowerComparison highlight={product.slug as "gt3" | "gt5" | "gt7"} />}

          {/* Applications */}
          <section className="mt-16">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Suitable applications</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Environments this platform is designed for. These are suitability examples, not descriptions of
              existing customer deployments.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {product.applications.map((app) => (
                <Card key={app.title} className="glass-effect border-[hsl(var(--line))] overflow-hidden">
                  {app.image && (
                    <img src={app.image} alt={app.title} loading="lazy" className="h-32 w-full object-cover" />
                  )}
                  <div className="p-4">
                    <h3 className="text-sm font-semibold">{app.title}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">{app.body}</p>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* Site assessment */}
          <section className="mt-16">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Site assessment and deployment</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { t: "Suitability review", b: "We walk the site and confirm whether this platform fits the surfaces, geometry and workload before anything is quoted." },
                { t: "Mapping", b: "Work areas, exclusions and transit routes are mapped and reviewed with your team." },
                { t: "Training", b: "Operator and supervisor training is scoped to your shifts and staffing." },
                { t: "Ongoing support", b: "Tell us what support level you need; terms are confirmed in your written proposal." },
              ].map((item) => (
                <div key={item.t} className="rounded-2xl border border-[hsl(var(--line))] bg-white/5 p-4">
                  <h3 className="text-sm font-semibold">{item.t}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{item.b}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Service inclusions, response times and commercial terms are defined in your Neil AI proposal — nothing
              is included by default on this page.
            </p>
            <Button className="mt-5 rounded-full" size="lg" onClick={() => setQuoteOpen(true)}>
              Request a site assessment
            </Button>
          </section>

          {/* FAQ */}
          <section className="mt-16">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-4">
              {product.faqs.map((faq, i) => (
                <AccordionItem key={faq.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-16">
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold">Related robots</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => r && (
                  <Card key={r.slug} className="glass-effect border-[hsl(var(--line))] overflow-hidden">
                    <img src={r.cardImage} alt={r.name} loading="lazy" className="h-40 w-full object-contain bg-black/20" />
                    <div className="p-4">
                      <h3 className="font-orbitron font-bold">{r.name}</h3>
                      <p className="mt-1 text-xs text-muted-foreground">{r.cardSummary}</p>
                      <Button asChild size="sm" variant="outline" className="mt-3 w-full rounded-full">
                        <Link to={`/robots/${r.slug}`}>View details</Link>
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* Final CTA */}
          <section className="mt-16 rounded-3xl border border-primary/25 bg-primary/5 p-8 text-center">
            <h2 className="font-orbitron text-2xl md:text-3xl font-bold">
              Scope {product.name} for your operation
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              Neil AI Global Solutions handles assessment, deployment, training and ongoing support. Tell us about
              the site and we will come back with a written proposal.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button size="lg" className="rounded-full" onClick={() => setQuoteOpen(true)}>Request a Quote</Button>
              <Button asChild size="lg" variant="outline" className="rounded-full">
                <a href="tel:3369624539">Call (336) 962-4539</a>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <PuduQuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        productName={product.name}
        variantLabel={product.variants ? variant?.label : undefined}
      />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-[96vw] sm:max-w-4xl p-2 sm:p-4">
          {lightbox && (
            <figure>
              <img src={lightbox.src} alt={lightbox.caption} className="max-h-[80vh] w-full object-contain" />
              <figcaption className="mt-2 text-center text-xs text-muted-foreground">{lightbox.caption}</figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </>
  );
};

export default PuduProduct;

export const puduProductSlugs = puduProducts.map((p) => p.slug);
